/**
 * Element rendered at `/:tenantSlug/reset-password` — the BRANDED public
 * set-password page (serves both the forgot-password reset and the
 * invitation first-time flows).
 *
 * Mirrors PublicTenantLogin's pre-render checks:
 * 1. Tenant must exist — unknown slugs render the generic 404, never a
 *    default-branded form.
 * 2. Collapse to the tenant whose pages its users' emails link to — the slug
 *    itself when it has a brand of its own (so the page wears the brand the
 *    email wore), else its company's canonical URL — PRESERVING the query
 *    string (it carries the token). It used to collapse to the company even
 *    for a branded sub-organization, so the page wore another brand than the
 *    email that linked to it.
 *
 * No already-authenticated redirect: a signed-in user may legitimately
 * follow a reset link (e.g. from the Settings self-service email).
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ResetPasswordPage } from '@modules/auth';
import { lookupTenantSlug, type SlugLookupResult } from './tenantResolver';
import { LoadingScreen } from '@/components/common/LoadingScreen';
import { TenantNotFound } from './TenantNotFound';

export function PublicTenantResetPassword() {
  const navigate = useNavigate();
  const { tenantSlug = '' } = useParams<{ tenantSlug?: string }>();
  const slug = tenantSlug.toLowerCase();

  const [lookup, setLookup] = useState<SlugLookupResult | null>(null);

  useEffect(() => {
    if (!slug) { setLookup({ status: 'not_found' }); return; }
    let cancelled = false;
    setLookup(null);
    (async () => {
      const r = await lookupTenantSlug(slug);
      if (!cancelled) setLookup(r);
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const canonical = lookup?.status === 'found'
    ? (lookup.data.brand_source_code || lookup.data.company_tenant_code || lookup.data.canonical_slug || '')
      .toLowerCase()
    : '';

  // Unbranded sub-tenant → its brand's page. Keep the search string — it holds the token.
  useEffect(() => {
    if (lookup?.status !== 'found') return;
    if (canonical && canonical !== slug) {
      navigate(
        { pathname: `/${canonical}/reset-password`, search: window.location.search },
        { replace: true },
      );
    }
  }, [lookup, canonical, slug, navigate]);

  if (lookup === null) return <LoadingScreen />;
  if (lookup.status !== 'found') return <TenantNotFound />;
  if (canonical && canonical !== slug) return <LoadingScreen />;

  return <ResetPasswordPage />;
}
